import { createContext, useContext, useMemo } from 'react';
import type {EncodeDataAttributeCallback} from '@sanity/react-loader';
import type { z } from 'zod';
import { zHomepageHeroSection, zCta, zRichTextSection, zFeatureListSection, zMediaContentSection, zDocumentListSection, zHeroSection, zFaqSection, zQuoteFormSection } from '#app/types/schema.ts';
import HomepageHero from './homepage-hero';
import FeatureListSection from './feature-list-section';
import MediaContentSection from './media-content';
import DocumentListSection from './documen-list-section';
import CtaSection from './cta-section';
import { SanityContent } from './rich-text';
import HeroSection from './hero-section';
import QuoteFormSection from './quot-form-section';
import { cn } from '#app/utils/misc.tsx';
import { stegaClean } from '@sanity/client/stega';
import FaqSection from './faq-section';


// Define the union type for all possible section data objects
type SectionData =
  | z.infer<typeof zHomepageHeroSection>
  | z.infer<typeof zCta>
  | z.infer<typeof zRichTextSection>
  | z.infer<typeof zFeatureListSection>
  | z.infer<typeof zMediaContentSection>
  | z.infer<typeof zDocumentListSection>
  | z.infer<typeof zCta>
  | z.infer<typeof zRichTextSection>
  | z.infer<typeof zHeroSection>
  | z.infer<typeof zFaqSection>
  | z.infer<typeof zQuoteFormSection>
  // Add other section data types here
  | { _type: string; _key?: string };

// Define the props for CmsSection
type CmsSectionProps = {
  data: SectionData;
  encodeDataAttribute?: EncodeDataAttributeCallback;
  index?: number;
};

// --- Component Map ---
// Maps Sanity _type names to React components
const sectionComponents: Record<string, React.ComponentType<{ data: any, encodeDataAttribute?:any }>> = {
  homepageHeroSection: HomepageHero,
  featureListSection: FeatureListSection,
  mediaContentSection: MediaContentSection,
  documentListSection : DocumentListSection,
  cta: CtaSection,
  richTextSection: SanityContent,
  heroSection: HeroSection,
  quoteFormSection: QuoteFormSection,
  faqSection: FaqSection
};

export function CmsSection({ data, encodeDataAttribute, index }: CmsSectionProps) {
  const Component = useMemo(() => (data?._type ? sectionComponents[data._type] : null), [data?._type]);
  // Basic check for essential fields
  if (!data?._type || !data?._key) {
    console.warn('Section data is missing _type or _key:', data);
    return null;
  }

  if (!Component) {
    // Log warning if component not found for a type
    console.warn(`No component found for section type "${data._type}"`);
    // Optionally render a placeholder in development
    if (process.env.NODE_ENV === 'development') {
        return <MissingSection type={data._type} />;
    }
    return null;
  }

  // --- Optional Wrapper ---
  // You could add a SectionWrapper here like Theme Fluid,
  // or handle wrappers within individual section components.
  // For simplicity now, we'll just render the component.

  // Render the found component, passing the section data
  return (
    <SectionWrapper data={data} encodeDataAttribute={encodeDataAttribute} index={index}>
      <Component data={data} encodeDataAttribute={encodeDataAttribute} />
    </SectionWrapper>
  );
}
const colorSchemes = {
  default: "bg-background text-foreground",
  primary: "bg-primary text-primary-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  muted: "bg-muted text-foreground",
  accent: "bg-accent text-accent-foreground",
}

function SectionWrapper(props: {
  children: React.ReactNode;
  data: CmsSectionProps['data'];
  encodeDataAttribute?: EncodeDataAttributeCallback;
  index?: number;
}) {
  const {children, data} = props;
  const sectionData = stegaClean(data);

    const removeMarginBottom = 'removeMarginBottom' in sectionData
    ? sectionData.removeMarginBottom as boolean : false;

  const sectionClasses = cn(
    "relative mb-24 sm:mb-32",
    removeMarginBottom && "mb-0 sm:mb-0",
  )
  return (
    <SectionContext.Provider
      value={{
        encodeDataAttribute: props.encodeDataAttribute,
        id: data._key,
        index: props.index,
      }}
    >
      <section
        className={sectionClasses}
        data-section-type={data._type}
        id={`section-${data._key}`}
      >
        {children}
      </section>
    </SectionContext.Provider>
  );
}

export const SectionContext = createContext<null | {
  encodeDataAttribute?: EncodeDataAttributeCallback;
  id?: string;
  index?: number;
}>(null);

export function useSection() {
  return useContext(SectionContext);
}

// Simple fallback for missing components in development
function MissingSection({ type }: { type: string }) {
    return (
      <section className="py-8 bg-red-100 text-red-800">
        <div className="container text-center">
          <p>Missing React component for Sanity section type: <strong>{type}</strong></p>
        </div>
      </section>
    );
}